import * as React from "react";
import { useState } from "react";
import { Copy, Check } from "lucide-react";

import { cn } from "@/lib/utils";

interface CopyableLinkProps {
  href: string;
  value: string;
  label?: React.ReactNode;
  title?: string;
  copyTitle?: string;
  emptyLabel?: React.ReactNode;
  className?: string;
  linkClassName?: string;
}

function CopyableLink({
  href,
  value,
  label,
  title,
  copyTitle = "Copy to clipboard",
  emptyLabel = <span className="text-slate-400 font-sans text-xs">-</span>,
  className,
  linkClassName,
}: CopyableLinkProps) {
  const [copied, setCopied] = useState(false);

  if (!value) {
    return <>{emptyLabel}</>;
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={cn("flex items-center gap-1.5", className)} onClick={e => e.stopPropagation()}>
      <a
        href={href}
        className={cn(
          "text-xs text-primary hover:underline font-sans truncate max-w-[200px] transition-none",
          linkClassName
        )}
        title={title}
      >
        {label ?? value}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="text-slate-400 hover:text-slate-700 shrink-0 p-0.5 rounded hover:bg-slate-100 cursor-pointer"
        title={copyTitle}
      >
        {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}

function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
    }
  }
  return phone;
}

interface EmailLinkProps {
  email: string | null;
}

function EmailLink({ email }: EmailLinkProps) {
  return (
    <CopyableLink
      href={email ? `mailto:${email}` : ""}
      value={email ?? ""}
      title={email ? `Email ${email}` : undefined}
      copyTitle="Copy email to clipboard"
    />
  );
}

interface PhoneLinkProps {
  phoneNumber: string | null;
}

function PhoneLink({ phoneNumber }: PhoneLinkProps) {
  return (
    <CopyableLink
      href={phoneNumber ? `sms:${phoneNumber}` : ""}
      value={phoneNumber ?? ""}
      label={phoneNumber ? formatPhoneNumber(phoneNumber) : undefined}
      title={phoneNumber ? `Text ${phoneNumber}` : undefined}
      copyTitle="Copy phone to clipboard"
    />
  );
}

CopyableLink.displayName = "CopyableLink";
EmailLink.displayName = "EmailLink";
PhoneLink.displayName = "PhoneLink";

export { EmailLink, PhoneLink };
